import {
  Button,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import {
  IdentityVerificationStatus,
  IndividualDocumentType,
  useGetSubscriptionQuery,
  useValidateKycDocumentsMutation,
} from '@blockpulse3/graphql/hooks';
import { ResponsiveModalFooter } from '@blockpulse3/ui/commons';

import { useUser } from '../../../../providers';
import { SelectVerificationType } from './SelectVerificationType';
import { UserVerificationApproved } from './UserVerificationApproved';
import { UserVerificationPending } from './UserVerificationPending';
import { DocumentUserVerification, UbbleUserVerification } from './VerificationType';
import { IVerificationType } from './types';

type Props = {
  onClose: () => void;
  isUserWorkspace: boolean;
};

export function VerificationStepUser({ onClose = noop, isUserWorkspace }: Props): JSX.Element {
  const i18nSubscriptionVerifyIdentityModal = useTranslations(
    'SubscriptionIdentityVerificationModal',
  );

  /* ** Set the verification component after user selection ** */
  const [verificationType, setVerificationType] = useState<IVerificationType>(null);
  /* ** Check whether all documents are uploaded in the case of a desktop verification ** */
  const [isDesktopValidable, setIsDesktopValidable] = useState(false);

  const { user } = useUser();

  const { subscriptionId = '' } = useParams();

  const { data } = useGetSubscriptionQuery({
    variables: { subscriptionId },
    skip: !subscriptionId,
  });
  const subscriptionIdentity = data?.subscription.identity;
  const individualIdentity =
    subscriptionIdentity?.individualIdentity ||
    subscriptionIdentity?.companyIdentity?.kybIndividualIdentity ||
    user?.individualIdentity;

  const kycVerificationStatus = individualIdentity?.kycVerificationStatus;
  const identityVerified = kycVerificationStatus === IdentityVerificationStatus.APPROVED;
  const identityPending =
    kycVerificationStatus &&
    [IdentityVerificationStatus.PROCESSING, IdentityVerificationStatus.PENDING].includes(
      kycVerificationStatus,
    );

  const [validateKycDocuments] = useValidateKycDocumentsMutation();

  useEffect(() => {
    const hasDesktopVerificationStarted = individualIdentity?.documents?.some((doc) =>
      [
        IndividualDocumentType.IDENTITY_DOCUMENT,
        IndividualDocumentType.IDENTITY_DOCUMENT_SECONDARY,
      ].includes(doc.type),
    );

    if (
      hasDesktopVerificationStarted &&
      kycVerificationStatus !== IdentityVerificationStatus.REFUSED
    ) {
      setVerificationType('document');
    }
  }, [individualIdentity, kycVerificationStatus]);

  const RenderVerificationModalContent = (): JSX.Element => {
    if (identityVerified) {
      return <UserVerificationApproved />;
    }
    if (identityPending) {
      return <UserVerificationPending />;
    }

    switch (verificationType) {
      case 'ubble':
        return <UbbleUserVerification onClose={onClose} />;
      case 'document':
        return (
          <DocumentUserVerification setIsValidable={setIsDesktopValidable} onSubmit={onClose} />
        );
      default:
        return <SelectVerificationType setVerificationType={setVerificationType} />;
    }
  };

  const handleKycDocumentSubmit = (): void => {
    if (!individualIdentity) return;

    validateKycDocuments({
      variables: {
        validateKycDocumentsInput: {
          individualIdentityId: individualIdentity.id,
        },
      },
      onCompleted: () => {
        onClose();
      },
    });
  };

  return (
    <ModalContent>
      <ModalHeader>
        <Text>{i18nSubscriptionVerifyIdentityModal('VerifyIdentity')}</Text>
        {!isUserWorkspace && (
          <Text color="secondary" fontSize="sm">
            {i18nSubscriptionVerifyIdentityModal('Steps', {
              stepCount: 2,
              totalStepCount: 2,
            })}
            {i18nSubscriptionVerifyIdentityModal('Step2')}
          </Text>
        )}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <RenderVerificationModalContent />
      </ModalBody>
      <Divider />
      <ResponsiveModalFooter>
        {verificationType === 'document' ? (
          <Button
            isDisabled={!isDesktopValidable && !identityVerified}
            width="full"
            onClick={handleKycDocumentSubmit}
          >
            {i18nSubscriptionVerifyIdentityModal('ValidateDocuments')}
          </Button>
        ) : (
          <Button width="full" onClick={onClose}>
            {i18nSubscriptionVerifyIdentityModal(identityVerified ? 'Validate' : 'Close')}
          </Button>
        )}
      </ResponsiveModalFooter>
    </ModalContent>
  );
}
